import { Grid, makeStyles } from '@material-ui/core';
import {
  V2FormComponentDef,
  V2FormTemplate,
  V2GroupComponentDef,
} from '@terragotech/form-renderer';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useConfig } from 'context/ConfigContext';
import { useParams } from 'react-router-dom';
import { CommandVersionDefinition } from '@terragotech/gen5-config-lib';
import { cloneDeep, isUndefined } from 'lodash';
import { TextHeaderEditForm } from 'components/FormDialog/TextHeaderEditForm';
import { AggregateContextProvider } from 'components/FormDialog/contexts/AggregateContext';
import { FormContextProvider } from 'components/FormDialog/contexts/FormContext';
import { successMsg } from 'components/SnackbarUtilsConfigurator';
import { GroupEditForm } from 'components/FormDialog/GroupEditForm';
import { TextInputEditForm } from 'components/FormDialog/TextInputEditForm';
import { MapAggregateSelector } from 'components/FormDialog/MapAggregateSelectorForm';
import { OptionsInputEditForm } from 'components/FormDialog/OptionsInputEditForm';
import { MediaEditForm } from 'components/FormDialog/MediaEditForm';
import { checkDuplicateFormName } from 'pages/aggregates/utils/formUtils';
import { V2FormComponentDefWithName } from 'pages/aggregates/components/CommandFormEditor/CommandFormList';
import { CommandFormEditor } from 'pages/aggregates/components/CommandFormEditor/CommandFormEditor';
import { colors } from 'utils/colors';
import { EditedDefData } from 'utils/types';
import { getAggregateIndex } from 'utils/navigationUtils';
import { useMapperRefChanger } from 'utils/useMapperRefChanger';
import { EmptyCommand } from 'views/pages/records/EmptyCommand';
import {
  FORM_REQUIRED_LABEL_ERROR_MESSAGE,
  FORM_REQUIRED_NAME_ERROR_MESSAGE,
  FORM_SAME_NAME_ERROR_MESSAGE,
} from 'utils/Utils';

export interface FormEditorEditedComponentProps {
  name: string;
  index: number;
  droppableId: string;
}

interface Props {
  formDefinition: V2FormTemplate;
  isImportCommand: boolean;
  isImport: boolean;
  setFormDefinition: Dispatch<SetStateAction<V2FormTemplate>>;
  formEditorEditedData: null | V2FormComponentDefWithName;
  setFormEditorEditedData: (val: null | V2FormComponentDefWithName) => void;
  formEditorEditedComponent: FormEditorEditedComponentProps | null;
  setFormEditorEditedComponent: (val: FormEditorEditedComponentProps | null) => void;
  setFormComponentNameError: (val: boolean) => void;
  setFormComponentEmptyLabelError: (val: boolean) => void;
  setFormComponentEmptyNameError: (val: boolean) => void;
  getEditedDef?: (val: EditedDefData) => V2FormTemplate | null;
  focusedItem: string;
  setFocusedItem: (val: string) => void;
  version: CommandVersionDefinition | undefined;
  currentEditItem: null | V2FormComponentDefWithName;
  setCurrentEditItem: (val: null | V2FormComponentDefWithName) => void;
}

const FormEditor = ({
  formDefinition,
  isImportCommand,
  isImport,
  setFormDefinition,
  formEditorEditedData,
  setFormEditorEditedData,
  formEditorEditedComponent,
  setFormEditorEditedComponent,
  setFormComponentNameError,
  setFormComponentEmptyNameError,
  setFormComponentEmptyLabelError,
  getEditedDef,
  focusedItem,
  setFocusedItem,
  currentEditItem,
  setCurrentEditItem,
}: Props) => {
  const classes = useStyles();
  const { config } = useConfig();
  const { aggregate: aggrName } = useParams() as {
    aggregate: string;
  };
  const aggrIndex = getAggregateIndex(config, aggrName);
  const currentAggregate = config?.aggregates?.[aggrIndex];
  const [orderCount, setOrderCount] = useState<number>(formDefinition.order.length || 0);
  const [existingNameError, setExistingNameError] = useState(false);
  const [emptyNameError, setEmptyNameError] = useState(false);
  const [emptyLabelError, setEmptyLabelError] = useState(false);
  const mapperRefChanger = useMapperRefChanger();

  useEffect(() => {
    setFormComponentNameError(existingNameError);
  }, [existingNameError, setFormComponentNameError]);

  useEffect(() => {
    setFormComponentEmptyNameError(emptyNameError);
  }, [emptyNameError, setFormComponentEmptyNameError]);

  useEffect(() => {
    setFormComponentEmptyLabelError(emptyLabelError);
  }, [emptyLabelError, setFormComponentEmptyLabelError]);

  const checkCurrentEditItem = useCallback(() => {
    if (currentEditItem) {
      const isPresent = formDefinition.order.includes(currentEditItem?.name);
      if (!isPresent && orderCount !== formDefinition.order.length) {
        setCurrentEditItem(null);
      }
    }
  }, [formDefinition.order, currentEditItem, orderCount, setCurrentEditItem]);

  useEffect(() => {
    if (currentEditItem) {
      setOrderCount(formDefinition.order.length);
    }
  }, [currentEditItem, formDefinition.order?.length]);

  useEffect(() => {
    checkCurrentEditItem();
  }, [formDefinition.order, currentEditItem, checkCurrentEditItem]);

  const handleSelectedItem = (
    event: React.MouseEvent | null,
    formComponentName: string,
    group?: string,
    item?: V2FormComponentDefWithName
  ) => {
    setFocusedItem(`${group || ''}${formComponentName}`);
    if (!isUndefined(item)) {
      setCurrentEditItem(item);
    }
  };
  const editItem = async (
    component: V2FormComponentDef,
    name: string,
    droppableId: string,
    index: number
  ) => {
    const comp = cloneDeep(component);
    const item: V2FormComponentDefWithName = { ...comp, name, droppableId, index };
    setCurrentEditItem(item);
  };

  const selectNextComponent = (
    droppableId: string,
    index: number,
    componentFormDefinition: V2FormTemplate,
    formDefinition?: V2FormTemplate
  ) => {
    // if formDefinition arg present, then it is group child
    let nextIndex = -1;
    if (index === componentFormDefinition.order.length - 1) {
      //if last index deleted, select previous component
      nextIndex = index - 1;
    } else {
      // select next component
      nextIndex = index + 1;
    }
    if (nextIndex !== -1) {
      const nextComponentName = componentFormDefinition.order[nextIndex];
      const component = componentFormDefinition.components[nextComponentName];
      setCurrentEditItem({ ...component, name: nextComponentName, index: nextIndex, droppableId });
      setFocusedItem(formDefinition ? `${droppableId}${nextComponentName}` : nextComponentName);
    } else {
      if (formDefinition) {
        // if group child deleted and no more childs for this group, then select group
        const component = formDefinition.components[droppableId];
        setCurrentEditItem({
          ...component,
          name: droppableId,
          index: nextIndex,
          droppableId: 'form',
        });
        setFocusedItem(droppableId);
      }
    }
  };

  const deleteItem = async (id: string, droppableId: string, index: number) => {
    selectNextComponent(droppableId, index, formDefinition);
    let form = cloneDeep(formDefinition);
    const removeItemFromForm = () => {
      form.order.splice(index, 1);
      delete form.components[id];
    };
    const removeItemFromGroup = () => {
      dropOrder.splice(index, 1);
      delete dropComponents[id];
    };
    successMsg(`Form element "${id}" has been successfully deleted`);
    if (droppableId === 'form') {
      removeItemFromForm();
      form = mapperRefChanger.removeFormReferences(form, id);
      setFormEditorEditedData(null);
      setFormEditorEditedComponent(null);
      setFormDefinition(form);
      return;
    }
    const {
      template: { order: dropOrder, components: dropComponents },
    } = form.components[droppableId] as V2GroupComponentDef;
    removeItemFromGroup();
    form = mapperRefChanger.removeFormReferences(form, id, droppableId);
    setFormEditorEditedData(null);
    setFormEditorEditedComponent(null);
    setFormDefinition(form);
  };

  const error = existingNameError
    ? FORM_SAME_NAME_ERROR_MESSAGE
    : emptyNameError
    ? FORM_REQUIRED_NAME_ERROR_MESSAGE
    : emptyLabelError
    ? FORM_REQUIRED_LABEL_ERROR_MESSAGE
    : '';

  return (
    <Grid container className={classes.gridCtn}>
      <Grid item sm={4} className={classes.formFieldContainer}>
        <CommandFormEditor
          formEditorEditedData={formEditorEditedData}
          formEditorEditedComponent={formEditorEditedComponent}
          setFormEditorEditedData={setFormEditorEditedData}
          formDefinition={formDefinition}
          isImportCommand={isImportCommand}
          isImport={isImport}
          setFormDefinition={(val) => setFormDefinition(val)}
          handleSelectedItem={handleSelectedItem}
          editItem={editItem}
          deleteItem={deleteItem}
          hasError={existingNameError || emptyNameError || emptyLabelError}
          error={error}
          focusedItem={focusedItem}
          setFormEditorEditedComponent={setFormEditorEditedComponent}
          selectNextComponent={selectNextComponent}
          setExistingNameError={setExistingNameError}
          setEmptyNameError={setEmptyNameError}
          setEmptyLabelError={setEmptyLabelError}
        />
      </Grid>
      <Grid item sm={8} className={classes.formDetailContainer}>
        {currentEditItem !== null ? (
          <AggregateContextProvider aggregateConfig={currentAggregate}>
            <FormContextProvider formDefinition={formDefinition}>
              <div className={classes.column}>
                {getFormComponent(
                  formDefinition,
                  existingNameError,
                  setExistingNameError,
                  emptyNameError,
                  setEmptyNameError,
                  emptyLabelError,
                  setEmptyLabelError,
                  currentEditItem,
                  isImportCommand,
                  getEditedDef,
                  setFormDefinition
                )}
              </div>
            </FormContextProvider>
          </AggregateContextProvider>
        ) : (
          <>
            <EmptyCommand
              title="The form is currently empty"
              description="No controls are currently configured in the workflow. Click the plus icon to add a new control."
            />
          </>
        )}
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles({
  gridCtn: {
    height: '100%',
  },
  formFieldContainer: {
    height: '100%',
    backgroundColor: colors.lotion,
    borderRight: `1px solid ${colors.black10}`,
  },
  formDetailContainer: {
    backgroundColor: colors.white,
    padding: 22,
    paddingLeft: 30,
    height: '100%',
    overflowY: 'scroll',
  },
  column: { display: 'flex', flexDirection: 'column' },
});

const getFormComponent = (
  formDefinition: V2FormTemplate,
  existingNameError: boolean,
  setExistingNameError: (hasError: boolean) => void,
  emptyNameError: boolean,
  setEmptyNameError: (hasError: boolean) => void,
  emptyLabelError: boolean,
  setEmptyLabelError: (hasError: boolean) => void,
  item: V2FormComponentDefWithName,
  isImportCommand?: boolean,
  getEditedDef?: (val: EditedDefData) => V2FormTemplate | null,
  setFormDefinition?: (val: V2FormTemplate) => void
) => {
  const checkDuplicateName = (name: string) => {
    return checkDuplicateFormName(name, item, formDefinition.components);
  };
  
  const handleNameChange = (name: string, setName: (name: string) => void) => {
    const isName = checkDuplicateName(name);
    const isEmpty = checkEmptyText(name);
    setExistingNameError(isName);
    setEmptyNameError(isEmpty);
    setName(name);
  };

  const handleLabelChange = (label: string, setLabel: (label: string) => void) => {
    const isEmpty = checkEmptyText(label);
    setEmptyLabelError(isEmpty);
    setLabel(label);
  };

  const checkEmptyText = (text: string) => {
    return text.trim() === '';
  };

  switch (item?.type) {
    case 'textheader':
      return (
        <TextHeaderEditForm
          existingNameError={existingNameError}
          emptyNameError={emptyNameError}
          handleNameChange={handleNameChange}
          emptyLabelError={emptyLabelError}
          handleLabelChange={handleLabelChange}
          component={item}
          getEditedDef={getEditedDef}
          setFormDefinition={setFormDefinition}
        />
      );
    case 'group':
      return (
        <GroupEditForm
          existingNameError={existingNameError}
          emptyNameError={emptyNameError}
          handleNameChange={handleNameChange}
          emptyLabelError={emptyLabelError}
          handleLabelChange={handleLabelChange}
          component={item}
          isImportCommand={isImportCommand}
          getEditedDef={getEditedDef}
          setFormDefinition={setFormDefinition}
        />
      );
    case 'textInput':
    case 'textArea':
    case 'numberInput':
      return (
        <TextInputEditForm
          getEditedDef={getEditedDef}
          setFormDefinition={setFormDefinition}
          existingNameError={existingNameError}
          emptyNameError={emptyNameError}
          handleNameChange={handleNameChange}
          emptyLabelError={emptyLabelError}
          handleLabelChange={handleLabelChange}
          component={item}
          isImportCommand={isImportCommand}
          autosuggest
        />
      );
    case 'computed':
      return (
        <TextInputEditForm
          existingNameError={existingNameError}
          emptyNameError={emptyNameError}
          handleNameChange={handleNameChange}
          emptyLabelError={emptyLabelError}
          handleLabelChange={handleLabelChange}
          component={item}
          isImportCommand={isImportCommand}
          computed
          getEditedDef={getEditedDef}
          setFormDefinition={setFormDefinition}
        />
      );
    case 'date':
    case 'time':
    case 'barcode':
    case 'location':
    case 'floorPlanLocation':
    case 'polyline':
      return (
        <TextInputEditForm
          existingNameError={existingNameError}
          emptyNameError={emptyNameError}
          handleNameChange={handleNameChange}
          emptyLabelError={emptyLabelError}
          handleLabelChange={handleLabelChange}
          component={item}
          isImportCommand={isImportCommand}
          getEditedDef={getEditedDef}
          setFormDefinition={setFormDefinition}
        />
      );
    case 'mapAggregateSelector':
      return (
        <MapAggregateSelector
          existingNameError={existingNameError}
          emptyNameError={emptyNameError}
          handleNameChange={handleNameChange}
          emptyLabelError={emptyLabelError}
          handleLabelChange={handleLabelChange}
          component={item}
          getEditedDef={getEditedDef}
          setFormDefinition={setFormDefinition}
        />
      );
    case 'radio':
    case 'select':
    case 'checkbox':
      return (
        <OptionsInputEditForm
          existingNameError={existingNameError}
          emptyNameError={emptyNameError}
          handleNameChange={handleNameChange}
          emptyLabelError={emptyLabelError}
          handleLabelChange={handleLabelChange}
          component={item}
          getEditedDef={getEditedDef}
          setFormDefinition={setFormDefinition}
        />
      );
    case 'audioupload':
    case 'imageupload':
    case 'fileupload':
    case 'videoupload':
      return (
        <MediaEditForm
          existingNameError={existingNameError}
          emptyNameError={emptyNameError}
          handleNameChange={handleNameChange}
          emptyLabelError={emptyLabelError}
          handleLabelChange={handleLabelChange}
          component={item}
          getEditedDef={getEditedDef}
          setFormDefinition={setFormDefinition}
        />
      );
    default:
      return null;
  }
};

export default FormEditor;
